/**
 * messageNames.ts
 *
 * How a Communication Center row names who it is from. Shared by the message
 * list (MessagesPage) and the overview's "Needs attention" list so the two
 * never name the same conversation differently.
 */
import type { CommLogEntry } from '@/services/communications.service';

/** A row with no patient behind it: an inbound message nobody has matched yet. */
export function isOrphan(entry: Pick<CommLogEntry, 'patientId'>): boolean {
  const id = entry.patientId;
  return id == null || id === 0;
}

// Unmatched inbound SMS store the sender's raw phone digits in strActivity
// (there's no name to show). Format 10/11-digit numbers for readability;
// anything else (an actual sender name/email) passes through unchanged.
export function formatUnmatchedSender(sender: string): string {
  const digits = sender.replace(/\D/g, '');
  if (digits !== sender || digits.length < 10 || digits.length > 11) return sender;
  const tenDigit = digits.length === 11 ? digits.slice(1) : digits;
  return `(${tenDigit.slice(0, 3)}) ${tenDigit.slice(3, 6)}-${tenDigit.slice(6)}`;
}

export function patientName(entry: CommLogEntry): string {
  const name = [entry.patientFirstName, entry.patientLastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (isOrphan(entry)) {
    const sender = entry.strActivity?.trim();
    if (sender) return formatUnmatchedSender(sender);
    return 'Unmatched sender';
  }
  return 'Unknown patient';
}
